/**
 * Communities (gh #4872): an org-owned community, held by one `CommunityDO`
 * (`community:{community_id}`), listed in its org's registry (`org_community`, at most
 * `COMMUNITIES_PER_ORG_MAX`).
 *
 * Only the platform admins create one (Decision 1): `POST /api/admin/communities`, through the
 * audited `wayai admin communities create` CLI. The org registers the community first, then its
 * `CommunityDO` is created; a registry row whose community was never created is dropped by a
 * later registry read. A community is destroyed with its org (Decision 17), or by the platform
 * admins (`DELETE /api/admin/communities/:communityId`, `wayai admin communities destroy`): its
 * `CommunityDO` first, then its registry row, which a later registry read drops too.
 *
 * MEMBERSHIP (Decisions 3 and 21). A signed-in person joins by the community's link,
 * `{app}/join?c={community_id}&k={token}`, which its org's owners and admins read, replace and
 * turn off (`/api/communities/:communityId/join-link`). The join page shows the community from
 * `GET /api/communities/join-preview`, which needs no sign-in; `POST …/join` makes the person a
 * member and `POST …/leave` ends it. Membership grants nothing else: no hub access, no org role.
 *
 * ROOMS AND MESSAGES (Decisions 4, 5, 7, 12 and 28). A community holds at most
 * `COMMUNITY_ROOMS_MAX` rooms, archived ones included, of three kinds: `discussion` (chat: members
 * post), `announcements` (the org's owners and admins and the moderators post) and `forum` (posts
 * and replies: FORUMS below). Its org's owners and admins create, rename and archive them
 * (`/api/communities/:communityId/rooms`), also while the communities switch is off.
 * A message is stored once, in its community, numbered by its room's own counter (`seq`): pages,
 * read positions and unread counts all use it, never a timestamp. Its author edits it (shown as
 * edited) and deletes it at any time, leaving a tombstone. A page leaves out the messages of
 * authors the reader blocks; unread counts do not (Decision 7).
 *
 * FORUMS (Decisions 26 and 29). A forum room's messages are posts (a title and a text) and replies
 * one level under a post; both are sent, edited and deleted as messages, under the same limits,
 * and both take the room's `seq`, so the room keeps one unread count. Posts are listed by their
 * latest activity (`last_activity_seq`: the post itself, or its newest reply), newest first; a
 * post with activity after the reader's read position in the room is new. A deleted post's
 * replies stop showing. The post's author, a moderator or an owner or admin of the org marks one
 * reply as the answer, and the list can hold only the posts with none (Unanswered) or those whose
 * title holds a search, ignoring case and accents.
 *
 * STATS (Decision 28). The org's owners and admins read the community's members, joins and new
 * accounts by the join link's source, and its messages per day, over the last 7 and 30 days.
 *
 * MODERATION (Decisions 2 and 9). The org's owners and admins are the community's admins; they
 * appoint moderators among its members and dismiss them (`/api/communities/:communityId/moderators`).
 * Admins outrank moderators, who outrank members, and no one acts on a person of their own rank or
 * above: a moderator never on an admin or another moderator. Moderators and admins delete any
 * message, remove a member, ban a person (a ban refuses their rejoining by the link, and can delete
 * every message they wrote) and lift a ban; lock a forum post; pin up to
 * `COMMUNITY_PINS_PER_ROOM_MAX` messages or posts per room; mark a post's answer; and post in
 * announcements rooms. Every moderation is logged (`/mod-log`, at most `COMMUNITY_MOD_ACTIONS_MAX`
 * entries, the oldest dropped). Admins also set the community's rules and slow mode, which
 * moderators and admins are not held to, and list their org's communities
 * (`/api/communities/orgs/:orgId`), also while the communities switch is off.
 *
 * REPORTS AND BLOCKS (Decisions 9 and 10). A member reports a message or a person through the
 * threads surface's report (`POST /api/threads/reports`, `people-reports.ts`), which reaches the
 * WayAI team and, without the reporter's identity, the moderators' inbox
 * (`/api/communities/:communityId/reports`, at most `COMMUNITY_REPORTS_MAX`, the oldest dropped);
 * a report about a moderator or an owner or admin of the org reaches only the WayAI team, and the
 * inbox leaves out every report about a person who holds either role when it is read. Moderators
 * and admins mark a report resolved once they have handled it. A member blocks another from the
 * community (`POST /api/contacts/blocks` with `community_id`), and pages then leave out the
 * blocked person's messages.
 */

import { z } from 'zod';
import { optionalTrimmedText, uuidSchema } from './common';
import { peopleUserIdSchema } from './people-contacts';
import type { CommunityReportTarget, PeopleReportReason } from './people-reports';

// =============================================================================
// LIMITS
// =============================================================================

/** The communities one org owns at most (Decision 27; the Caps table). */
export const COMMUNITIES_PER_ORG_MAX = 10;
/** The longest community name, in characters. */
export const COMMUNITY_NAME_MAX_LENGTH = 100;

/**
 * The `details.reason` of the 409 a create gets when the org already owns
 * `COMMUNITIES_PER_ORG_MAX` communities.
 */
export const COMMUNITIES_PER_ORG_REASON = 'communities_per_org';

/**
 * The `details.reason` of the 409 a create gets once a deletion of the org has closed its
 * community registry (#4872 owner-delegated decision 3). The registry is never reopened.
 */
export const COMMUNITY_REGISTRY_CLOSED_REASON = 'community_registry_closed';

/** The active members one community holds at most (the Caps table). */
export const COMMUNITY_MEMBERS_MAX = 10_000;
/** The communities one person is an active member of at most (the Caps table). */
export const COMMUNITIES_PER_PERSON_MAX = 50;

/** The `details.reason` of the 409 a join gets when the community holds `COMMUNITY_MEMBERS_MAX` members. */
export const COMMUNITY_FULL_REASON = 'community_full';
/** The `details.reason` of the 409 a join gets when the person is in `COMMUNITIES_PER_PERSON_MAX` communities. */
export const COMMUNITIES_PER_PERSON_REASON = 'communities_per_person';

/** The longest join-link source tag (`?src=`), in characters (Decision 28). */
export const COMMUNITY_JOIN_SOURCE_MAX_LENGTH = 32;
/** The longest join-flow token a join takes; one the preview mints is shorter. */
export const COMMUNITY_JOIN_FLOW_TOKEN_MAX_LENGTH = 128;

/** The rooms one community holds at most, archived ones included (the Caps table). */
export const COMMUNITY_ROOMS_MAX = 20;
/** The longest room name, in characters. */
export const COMMUNITY_ROOM_NAME_MAX_LENGTH = 100;
/** The `details.reason` of the 409 a room's creation gets when the community holds `COMMUNITY_ROOMS_MAX` rooms. */
export const COMMUNITY_ROOMS_REASON = 'rooms_per_community';

/** The longest message body, in characters (as threads'). */
export const COMMUNITY_MESSAGE_MAX_LENGTH = 4_000;
/**
 * The messages one page reads (≤ 50 × 4,000 characters per answer: the 32 MiB rule). A page
 * leaves out the messages of authors the reader blocks, so it may hold fewer, while its cursor
 * still moves past every message it read.
 */
export const COMMUNITY_MESSAGES_PAGE_SIZE = 50;
/**
 * The messages, tombstones excluded, one community holds at most (the Caps table). At the cap a
 * send answers 429 (`details.limit`: `COMMUNITY_LIVE_MESSAGES_LIMIT`) until an archival design
 * exists; deleting a message frees its place.
 */
export const COMMUNITY_LIVE_MESSAGES_MAX = 500_000;
/** Messages one community takes per second, from all its members together. */
export const COMMUNITY_MESSAGES_PER_SECOND = 5;
/** Messages one member sends in one community per UTC day. */
export const COMMUNITY_MEMBER_MESSAGES_PER_DAY = 500;
/** The same limit in a member's first day: the 24 hours after they joined. */
export const COMMUNITY_NEW_MEMBER_MESSAGES_PER_DAY = 20;

/**
 * The `details.reason` of the 409 a message gets in a room that takes no such message from the
 * caller: an announcements room from anyone but the org's owners and admins and the moderators, a
 * forum room a message that is neither a post nor a reply, and any other room a post or a reply.
 * A posts or replies page of a room that is not a forum, and a messages page of one that is, get
 * it too.
 */
export const COMMUNITY_ROOM_KIND_REASON = 'room_kind';
/** The `details.reason` of the 409 a message or an edit gets in an archived room. */
export const COMMUNITY_ROOM_ARCHIVED_REASON = 'room_archived';
/**
 * The `details.reason` of the 409 an owner or admin of the community's org gets on a message
 * before joining: they read every room as they are, and post after joining (Decision 2).
 */
export const COMMUNITY_NOT_JOINED_REASON = 'not_joined';

/** The `details.limit` of each 429 a message can get. */
export const COMMUNITY_RATE_LIMIT = 'community_rate';
export const COMMUNITY_DAILY_LIMIT = 'messages_per_day';
export const COMMUNITY_SLOW_MODE_LIMIT = 'slow_mode';
export const COMMUNITY_LIVE_MESSAGES_LIMIT = 'live_messages';

/** The longest forum post title, in characters (the Caps table). */
export const COMMUNITY_POST_TITLE_MAX_LENGTH = 200;
/** The characters of a post's text its listing shows (`CommunityPostListing.preview`). */
export const COMMUNITY_POST_PREVIEW_LENGTH = 200;
/**
 * The posts one posts page reads (≤ 50 listings of a 200-character title and preview: the 32 MiB
 * rule). Like a messages page, it leaves out the posts of authors the reader blocks, while its
 * cursor moves past every post it read. A replies page reads `COMMUNITY_MESSAGES_PAGE_SIZE`.
 */
export const COMMUNITY_POSTS_PAGE_SIZE = 50;
/** The longest title search (`q`), in characters (the Caps table). */
export const COMMUNITY_POST_SEARCH_MAX_LENGTH = 100;

/**
 * The `details.reason` of the 409 a message gets when the message it names as a post is not one:
 * a reply to a reply (replies are one level deep, Decision 26), or a title given to a message
 * that is not a post.
 */
export const COMMUNITY_NOT_A_POST_REASON = 'not_a_post';
/** The `details.reason` of the 409 a reply to a locked post gets. */
export const COMMUNITY_POST_LOCKED_REASON = 'post_locked';

/** A membership's status (`community_member.status`). */
export const COMMUNITY_MEMBER_STATUSES = ['active', 'left', 'removed', 'banned'] as const;
export type CommunityMemberStatus = (typeof COMMUNITY_MEMBER_STATUSES)[number];

/**
 * A member's role in their community (`community_member.role`, Decision 2): a moderator, appointed
 * by the org's owners and admins, or a member. A new membership starts as a member.
 */
export const COMMUNITY_MEMBER_ROLES = ['moderator', 'member'] as const;
export type CommunityMemberRole = (typeof COMMUNITY_MEMBER_ROLES)[number];

/**
 * The viewer's standing in a community they belong to, highest first: an owner or admin of the
 * community's org (`admin`, Decision 2: never a hub admin or team user of it), a moderator, or a
 * member.
 */
export const COMMUNITY_VIEWER_ROLES = ['admin', 'moderator', 'member'] as const;
export type CommunityViewerRole = (typeof COMMUNITY_VIEWER_ROLES)[number];

/** The messages or forum posts one room pins at most (the Caps table). */
export const COMMUNITY_PINS_PER_ROOM_MAX = 3;
/** The `details.reason` of the 409 a pin gets when its room already pins `COMMUNITY_PINS_PER_ROOM_MAX`. */
export const COMMUNITY_PINS_REASON = 'pins_per_room';

/**
 * The `details.reason` of the 409 a moderation gets when its actor does not outrank the person it
 * acts on (Decision 2: an admin outranks a moderator, who outranks a member; no one acts on their
 * own rank or above).
 */
export const COMMUNITY_RANK_REASON = 'rank';

/** The longest rules text, in characters. */
export const COMMUNITY_RULES_MAX_LENGTH = 4_000;
/** The longest slow mode, in seconds (6 hours); 0 turns it off. */
export const COMMUNITY_SLOW_MODE_MAX_SECONDS = 21_600;

/** The moderation log's entries one community keeps; past it the oldest go (the Caps table). */
export const COMMUNITY_MOD_ACTIONS_MAX = 10_000;
/** The moderation log's entries one page reads (the Caps table). */
export const COMMUNITY_MOD_ACTIONS_PAGE_SIZE = 50;
/** The people one moderators or bans page lists (the Caps table's members page). */
export const COMMUNITY_PEOPLE_PAGE_SIZE = 100;

/** The reports one community's inbox keeps; past it the oldest go (the Caps table). */
export const COMMUNITY_REPORTS_MAX = 10_000;
/** The reports one inbox page reads (the Caps table). */
export const COMMUNITY_REPORTS_PAGE_SIZE = 50;

/** What a moderation log entry records (`CommunityModAction.action`). */
export const COMMUNITY_MOD_ACTION_KINDS = [
  'delete_message',
  'remove_member',
  'ban_member',
  'lift_ban',
  'lock_post',
  'unlock_post',
  'pin_message',
  'unpin_message',
  'appoint_moderator',
  'dismiss_moderator',
] as const;
export type CommunityModActionKind = (typeof COMMUNITY_MOD_ACTION_KINDS)[number];

/** The days the community's stats count back (the Caps table's stats window). */
export const COMMUNITY_STATS_DAYS = 30;
/** The join-link sources the stats name, most joins first; the totals count them all. */
export const COMMUNITY_STATS_SOURCES_MAX = 100;

// =============================================================================
// REQUEST
// =============================================================================

export const communityNameSchema = z.string().trim().min(1).max(COMMUNITY_NAME_MAX_LENGTH);

/** POST /api/admin/communities — a new community owned by the org `org_id`. */
export const adminCreateCommunityBody = z
  .object({ org_id: uuidSchema, name: communityNameSchema })
  .strict();

export type AdminCreateCommunityBody = z.input<typeof adminCreateCommunityBody>;

/** A route on one community: `/api/communities/:communityId/…`. */
export const communityIdParam = z.object({ communityId: uuidSchema });

/**
 * A join link's token (`k`): 128 random bits as 32 lowercase hex characters. The link is posted
 * publicly; the token's job is replacement and disabling, not secrecy (#4872 owner-delegated
 * decision 7).
 */
export const communityJoinTokenSchema = z.string().regex(/^[0-9a-f]{32}$/);

/**
 * A join link's source tag (`?src=`, Decision 28), recorded with the membership: at most
 * `COMMUNITY_JOIN_SOURCE_MAX_LENGTH` characters of `[a-z0-9_-]`.
 */
export const communityJoinSourceSchema = z
  .string()
  .regex(new RegExp(`^[a-z0-9_-]{1,${COMMUNITY_JOIN_SOURCE_MAX_LENGTH}}$`));

/** GET /api/communities/join-preview — the community a join link names. */
export const communityJoinPreviewQuery = z.object({ c: uuidSchema, k: communityJoinTokenSchema });

/**
 * POST /api/communities/:communityId/join. `token` is the link's `k`; `src` its source tag.
 * `flow_token` is the `flow_token` of the FIRST preview the join page fetched, carried through
 * sign-up: the membership records `account_new` only when it shows the account did not exist
 * yet when that preview was served (#4872 owner-delegated decision 8). One that is missing,
 * invalid, expired or another community's records `false`; it never refuses the join.
 */
export const communityJoinBody = z
  .object({
    token: communityJoinTokenSchema,
    src: communityJoinSourceSchema.optional(),
    flow_token: z.string().max(COMMUNITY_JOIN_FLOW_TOKEN_MAX_LENGTH).optional(),
  })
  .strict();

export type CommunityJoinBody = z.input<typeof communityJoinBody>;

/** A room's kind (Decisions 4 and 26). It never changes. */
export const communityRoomKindSchema = z.enum(['discussion', 'forum', 'announcements']);
export type CommunityRoomKind = z.infer<typeof communityRoomKindSchema>;

export const communityRoomNameSchema = z.string().trim().min(1).max(COMMUNITY_ROOM_NAME_MAX_LENGTH);

/** A route on one room: `/api/communities/:communityId/rooms/:roomId/…`. */
export const communityRoomIdParam = z.object({ communityId: uuidSchema, roomId: uuidSchema });

/** A route on one message: `/api/communities/:communityId/rooms/:roomId/messages/:messageId`. */
export const communityMessageIdParam = z.object({ communityId: uuidSchema, roomId: uuidSchema, messageId: uuidSchema });

/** POST /api/communities/:communityId/rooms — an org admin's new room. */
export const communityRoomCreateBody = z
  .object({ kind: communityRoomKindSchema, name: communityRoomNameSchema })
  .strict();

export type CommunityRoomCreateBody = z.input<typeof communityRoomCreateBody>;

/** PATCH /api/communities/:communityId/rooms/:roomId — an org admin renames a room, or archives it or brings it back. */
export const communityRoomUpdateBody = z
  .object({ name: communityRoomNameSchema.optional(), archived: z.boolean().optional() })
  .strict()
  .refine((b) => b.name !== undefined || b.archived !== undefined, 'nothing to change');

export type CommunityRoomUpdateBody = z.input<typeof communityRoomUpdateBody>;

/**
 * GET /api/communities/:communityId/rooms/:roomId/messages — one page of
 * `COMMUNITY_MESSAGES_PAGE_SIZE` messages: the newest with neither cursor; those before `before`
 * (a `seq`), toward older ones; or those after `after`, toward newer ones — the gap a client
 * fills after it reconnects. Never both.
 */
export const communityMessagesQuery = z
  .object({
    before: z.coerce.number().int().min(1).optional(),
    after: z.coerce.number().int().min(0).optional(),
  })
  .refine((q) => q.before === undefined || q.after === undefined, 'before and after cannot be combined');

/** A message's text: at most `COMMUNITY_MESSAGE_MAX_LENGTH` characters, not only whitespace. */
export const communityMessageBodySchema = z
  .string()
  .max(COMMUNITY_MESSAGE_MAX_LENGTH)
  .refine((body) => body.trim().length > 0, 'must not be empty');

/** A forum post's title: at most `COMMUNITY_POST_TITLE_MAX_LENGTH` characters, trimmed, not empty. */
export const communityPostTitleSchema = z.string().trim().min(1).max(COMMUNITY_POST_TITLE_MAX_LENGTH);

/**
 * POST /api/communities/:communityId/rooms/:roomId/messages. `client_message_id` makes a retried
 * send idempotent: a second send with the same id from the same author in the same room answers
 * the first message. In a forum room a message is a post, with a `title`, or a reply to one, with
 * the post's id as `parent_message_id` (Decision 26); in any other room it has neither.
 */
export const communitySendBody = z
  .object({
    body: communityMessageBodySchema,
    client_message_id: uuidSchema.optional(),
    title: communityPostTitleSchema.optional(),
    parent_message_id: uuidSchema.optional(),
  })
  .strict()
  .refine((b) => b.title === undefined || b.parent_message_id === undefined, 'a post is not a reply: send a title or a parent_message_id, not both');

export type CommunitySendBody = z.input<typeof communitySendBody>;

/**
 * PATCH /api/communities/:communityId/rooms/:roomId/messages/:messageId — its author's new text,
 * and for a forum post, optionally, its new title (Decision 28).
 */
export const communityEditBody = z
  .object({ body: communityMessageBodySchema, title: communityPostTitleSchema.optional() })
  .strict();

export type CommunityEditBody = z.input<typeof communityEditBody>;

/** A route on one forum post: `/api/communities/:communityId/rooms/:roomId/posts/:postId/…`. */
export const communityPostIdParam = z.object({ communityId: uuidSchema, roomId: uuidSchema, postId: uuidSchema });

/**
 * GET /api/communities/:communityId/rooms/:roomId/posts — one page of `COMMUNITY_POSTS_PAGE_SIZE`
 * posts, latest activity first: the newest with no cursor, or those whose latest activity is
 * before `before_activity` (a `last_activity_seq`). `q`, trimmed, keeps only the posts whose title
 * holds it, ignoring case and accents (`%` and `_` match themselves; empty is no search); `unanswered`
 * only those with no answer (Decision 29).
 */
export const communityPostsQuery = z.object({
  before_activity: z.coerce.number().int().min(1).optional(),
  q: optionalTrimmedText(COMMUNITY_POST_SEARCH_MAX_LENGTH),
  unanswered: z.enum(['true', 'false']).optional().transform((value) => value === 'true'),
});

/**
 * PUT /api/communities/:communityId/rooms/:roomId/posts/:postId/answer — the post's author, a
 * moderator or an owner or admin of the org marks `reply_id`, one of the post's replies, as its answer, or clears
 * it with null (Decision 29).
 */
export const communityAnswerBody = z.object({ reply_id: uuidSchema.nullable() }).strict();

export type CommunityAnswerBody = z.input<typeof communityAnswerBody>;

/** POST /api/communities/:communityId/rooms/:roomId/read — everything up to `seq` has been read. */
export const communityReadBody = z.object({ seq: z.number().int().min(0) }).strict();

export type CommunityReadBody = z.input<typeof communityReadBody>;

/** A route on one person in a community: `/api/communities/:communityId/{moderators,members,bans}/:userId`. */
export const communityPersonParam = z.object({ communityId: uuidSchema, userId: peopleUserIdSchema });

/** A route on one org's communities: `/api/communities/orgs/:orgId`. */
export const communityOrgParam = z.object({ orgId: uuidSchema });

/**
 * GET /api/communities/:communityId/{moderators,bans} — one page of `COMMUNITY_PEOPLE_PAGE_SIZE`
 * people, by user id: the first with no cursor, or those after `after` (a user id).
 */
export const communityPeopleQuery = z.object({ after: peopleUserIdSchema.optional() });

/**
 * PATCH /api/communities/:communityId/settings — an org admin's rules (trimmed; empty or null
 * clears them) and slow mode: the seconds a member waits between two messages in one room, 0 off.
 */
export const communitySettingsBody = z
  .object({
    rules: z.string().trim().max(COMMUNITY_RULES_MAX_LENGTH).nullable().optional()
      .transform((rules) => (rules === '' ? null : rules)),
    slow_mode_seconds: z.number().int().min(0).max(COMMUNITY_SLOW_MODE_MAX_SECONDS).optional(),
  })
  .strict()
  .refine((b) => b.rules !== undefined || b.slow_mode_seconds !== undefined, 'nothing to change');

export type CommunitySettingsBody = z.input<typeof communitySettingsBody>;

/**
 * PUT /api/communities/:communityId/bans/:userId — a moderator or admin bans the person; with
 * `delete_messages`, every message they wrote in the community is deleted too (Decision 9).
 */
export const communityBanBody = z.object({ delete_messages: z.boolean().optional() }).strict();

export type CommunityBanBody = z.input<typeof communityBanBody>;

/** PUT /api/communities/:communityId/rooms/:roomId/posts/:postId/lock — lock or unlock a forum post. */
export const communityLockBody = z.object({ locked: z.boolean() }).strict();

export type CommunityLockBody = z.input<typeof communityLockBody>;

/** PUT /api/communities/:communityId/rooms/:roomId/messages/:messageId/pin — pin or unpin a message or post. */
export const communityPinBody = z.object({ pinned: z.boolean() }).strict();

export type CommunityPinBody = z.input<typeof communityPinBody>;

/**
 * GET /api/communities/:communityId/mod-log — one page of `COMMUNITY_MOD_ACTIONS_PAGE_SIZE`
 * entries, newest first: the newest with no cursor, or those before `before` (an `action_id`).
 */
export const communityModLogQuery = z.object({ before: z.coerce.number().int().min(1).optional() });

/**
 * GET /api/communities/:communityId/reports — the moderators' inbox, newest first: `before` is the
 * `next_before` of the page before; `pending` keeps the reports not yet resolved.
 */
export const communityReportsQuery = communityModLogQuery.extend({
  pending: z.enum(['true', 'false']).optional().transform((value) => value === 'true'),
});

/** POST /api/communities/:communityId/reports/:reportId/resolve */
export const communityReportIdParam = z.object({ communityId: uuidSchema, reportId: uuidSchema });

// =============================================================================
// RESPONSE
// =============================================================================

/**
 * A community as its `CommunityDO` holds it. `org_name` is the owning org's name, copied when
 * the community is created.
 */
export interface CommunitySummary {
  community_id: string;
  org_id: string;
  org_name: string;
  name: string;
  created_at: string;
}

/** POST /api/admin/communities — 201. */
export interface AdminCreateCommunityResponse {
  data: CommunitySummary;
}

/**
 * DELETE /api/admin/communities/:communityId — 200: the community, and when it was destroyed
 * (the first destroy's time, when it was already destroyed). Its members' index rows go, and then
 * its storage, after the response.
 */
export interface AdminDestroyCommunityResponse {
  data: CommunitySummary & { destroyed_at: string };
}

/**
 * GET /api/communities/join-preview — what the join page shows. `avatar_url` and `rules` are null
 * until the community's settings set them. `flow_token` is a signed record of when this preview
 * was served, for the join (`communityJoinBody`); it is absent when the server holds no key to
 * sign it with.
 */
export interface CommunityJoinPreview {
  community_id: string;
  name: string;
  avatar_url: string | null;
  org_name: string;
  member_count: number;
  rules: string | null;
  flow_token?: string;
}

export interface CommunityJoinPreviewResponse {
  data: CommunityJoinPreview;
}

/** The caller's membership, as a join answers it. */
export interface CommunityMembership {
  community_id: string;
  joined_at: string;
}

/** POST /api/communities/:communityId/join — 200, also for a member already in. */
export interface CommunityJoinResponse {
  data: CommunityMembership;
}

/**
 * A community's join link, as its org's owners and admins see it. `url` is null while the link
 * is off or was never made; replacing it makes a new one and turns it on.
 */
export interface CommunityJoinLink {
  enabled: boolean;
  url: string | null;
}

/** GET, POST and DELETE /api/communities/:communityId/join-link. */
export interface CommunityJoinLinkResponse {
  data: CommunityJoinLink;
}

/** A room, as its community's org admins manage it. */
export interface CommunityRoom {
  room_id: string;
  kind: CommunityRoomKind;
  name: string;
  /** An archived room takes no new message; its history stays readable. */
  archived: boolean;
  created_at: string;
}

/** GET /api/communities/:communityId/rooms — every room, archived ones included, oldest first. */
export interface CommunityRoomsResponse {
  data: CommunityRoom[];
}

/** POST /api/communities/:communityId/rooms — 201; PATCH …/rooms/:roomId — 200. */
export interface CommunityRoomResponse {
  data: CommunityRoom;
}

/**
 * A room as one reader sees it in their community's head: its last message's `seq` and theirs,
 * the last one they read. `last_seq - read_seq` is their unread count there
 * (`communityRoomUnreadCount`): it counts every message after their read position, those by
 * authors they block included (Decision 7), and their own sends move it forward.
 */
export interface CommunityRoomHead extends CommunityRoom {
  last_seq: number;
  read_seq: number;
}

/**
 * One of the caller's communities with its rooms: what the Community nav item, its switcher and
 * the community's header show. `member_count` is its active members; `role` is the caller's
 * standing there (owner-delegated decision 17), which decides what they may do: an `admin` (an
 * owner or admin of its org) and a `moderator` post in announcements, mark any post's answer and
 * moderate; a `member` posts where members post.
 */
export interface CommunityHead {
  community_id: string;
  name: string;
  avatar_url: string | null;
  org_id: string;
  org_name: string;
  member_count: number;
  role: CommunityViewerRole;
  rooms: CommunityRoomHead[];
}

/** GET /api/communities — the caller's communities (at most `COMMUNITIES_PER_PERSON_MAX`), by name. */
export interface CommunitiesResponse {
  data: CommunityHead[];
}

/**
 * The session snapshot's communities (Decision 7): the head of every community the viewer is an
 * active member of, empty when none. Absent from the snapshot while the communities surface is
 * off, for a caller that is not an interactive session, and when a head could not be read in
 * time (`GET /api/communities` serves the same list).
 */
export interface CommunitiesSessionSnapshot {
  heads: CommunityHead[];
}

/**
 * The author of a message, as every member sees them: a display name, never an email
 * (Decision 11). A null `display_name` renders as "WayAI user"; `deleted_user: true` as "Deleted
 * user" (Decision 12).
 */
export interface CommunityAuthor {
  user_id: string;
  display_name: string | null;
  deleted_user: boolean;
}

/**
 * One message. A deleted one stays as a tombstone: `body` (and a post's `title`) null, `deleted`
 * true (Decision 12). `edited_at` is set once its author has changed its text or title (Decision
 * 28). `client_message_id` is the author's own idempotency key, shown to the author alone. In a
 * forum room a post has a `title`, and a reply the post's id as `parent_message_id`.
 */
export interface CommunityMessage {
  message_id: string;
  room_id: string;
  seq: number;
  author_user_id: string;
  body: string | null;
  deleted: boolean;
  created_at: string;
  edited_at: string | null;
  deleted_at: string | null;
  client_message_id: string | null;
  title: string | null;
  parent_message_id: string | null;
}

/**
 * A page of messages, oldest first, without those of authors the reader blocks; `authors` names
 * the author of each one. `next_before_seq` continues toward older messages (`before`) and
 * `next_after_seq` toward newer ones (`after`); each is null when there are none that way. Both
 * come from every message the page read, those left out included, so a page can hold fewer than
 * `COMMUNITY_MESSAGES_PAGE_SIZE` messages, or none, and still continue.
 */
export interface CommunityMessagesPage {
  messages: CommunityMessage[];
  authors: CommunityAuthor[];
  next_before_seq: number | null;
  next_after_seq: number | null;
}

/** GET /api/communities/:communityId/rooms/:roomId/messages */
export interface CommunityMessagesResponse {
  data: CommunityMessagesPage;
}

/** POST …/messages (sent), PATCH and DELETE …/messages/:messageId — the message as it now stands. */
export interface CommunityMessageResponse {
  data: CommunityMessage;
}

/** POST …/rooms/:roomId/read — the room as the caller now sees it. */
export interface CommunityRoomHeadResponse {
  data: CommunityRoomHead;
}

/** What a forum post carries beyond a message (Decisions 26 and 29). */
export interface CommunityPostActivity {
  /** Its replies, deleted ones excluded, those of authors the reader blocks included. */
  reply_count: number;
  /** The `seq` of its latest activity: its newest reply's, or its own. Posts are listed by it. */
  last_activity_seq: number;
  /** When that activity was sent. */
  last_activity_at: string;
  /** Locked by a moderator: it takes no more replies. */
  locked: boolean;
}

/** A forum post with its whole text, as its replies page answers it. */
export interface CommunityPost extends CommunityMessage, CommunityPostActivity {
  title: string;
  /** The reply marked as its answer, or null (Decision 29). */
  answer_message_id: string | null;
}

/** A forum post as the posts page lists it: its title and the start of its text. */
export interface CommunityPostListing
  extends CommunityPostActivity, Pick<CommunityMessage, 'message_id' | 'room_id' | 'seq' | 'author_user_id' | 'created_at' | 'edited_at'> {
  title: string;
  /** The first `COMMUNITY_POST_PREVIEW_LENGTH` characters (code points) of its text. */
  preview: string;
  /** A reply is marked as its answer. */
  answered: boolean;
  /**
   * It has activity after the reader's read position in the room — since their last visit
   * (Decision 26). An org owner or admin who has not joined has no position: every post is new.
   */
  new: boolean;
}

/**
 * A page of posts, latest activity first, without those of authors the reader blocks; `authors`
 * names the author of each one. `next_before_activity` continues toward older activity
 * (`before_activity`), null when there is none; it comes from every post the page read, those
 * left out included, so a page can hold fewer than `COMMUNITY_POSTS_PAGE_SIZE`, or none, and still
 * continue. Posts are ordered by activity that moves: one replied to while a reader pages back
 * moves to the top, where the next newest page shows it.
 */
export interface CommunityPostsPage {
  posts: CommunityPostListing[];
  authors: CommunityAuthor[];
  next_before_activity: number | null;
}

/** GET /api/communities/:communityId/rooms/:roomId/posts */
export interface CommunityPostsResponse {
  data: CommunityPostsPage;
}

/**
 * A page of a post's replies (`messages`, paged as a room's messages are), with the post itself
 * and its answer, which the post shows first (Decision 29) and which also stays in its place among
 * the replies. `answer` is null when none is marked or when the reader blocks its author;
 * `authors` names the post's, the answer's and each reply's author.
 */
export interface CommunityRepliesPage extends CommunityMessagesPage {
  post: CommunityPost;
  answer: CommunityMessage | null;
}

/** GET /api/communities/:communityId/rooms/:roomId/posts/:postId/replies */
export interface CommunityRepliesResponse {
  data: CommunityRepliesPage;
}

/** PUT …/posts/:postId/answer — the post as it now stands. */
export interface CommunityPostResponse {
  data: CommunityPost;
}

/** Joins and the new accounts among them (`account_new`, Decision 28) over the last 7 and 30 days. */
export interface CommunityJoinCounts {
  joins_7d: number;
  joins_30d: number;
  new_accounts_7d: number;
  new_accounts_30d: number;
}

/** One join-link source tag's joins (`source` null: a join with none). */
export interface CommunitySourceStats extends CommunityJoinCounts {
  source: string | null;
}

/** One UTC day's messages, deleted ones included: chat and announcements, forum posts, forum replies. */
export interface CommunityDayStats {
  day: string;
  messages: number;
  posts: number;
  replies: number;
}

/**
 * A community's stats, as its org's owners and admins see them (Decision 28). A join is a
 * membership that started in the window; a person who joined again counts once, at their latest
 * join. Members active in the last 7 days are active members whose page reads stamped them seen
 * (at most once an hour).
 */
export interface CommunityStats {
  members: Record<CommunityMemberStatus, number>;
  active_members_7d: number;
  /** Every join, whatever its source. */
  joins: CommunityJoinCounts;
  /** The sources joined through in the last 30 days, most joins first, at most `COMMUNITY_STATS_SOURCES_MAX`. */
  joins_by_source: CommunitySourceStats[];
  /** The last `COMMUNITY_STATS_DAYS` UTC days, today included, oldest first. */
  days: CommunityDayStats[];
}

/** GET /api/communities/:communityId/stats */
export interface CommunityStatsResponse {
  data: CommunityStats;
}

/** A community's settings, as its org's owners and admins set them. */
export interface CommunitySettings {
  /** Shown on the join page; null for none. */
  rules: string | null;
  /** The seconds a member waits between two messages in one room; 0 is off. Moderators and admins are not held to it. */
  slow_mode_seconds: number;
}

/** GET and PATCH /api/communities/:communityId/settings */
export interface CommunitySettingsResponse {
  data: CommunitySettings;
}

/**
 * A page of people (`COMMUNITY_PEOPLE_PAGE_SIZE`), by user id: a community's moderators, or the
 * people it bans. `next_after` continues it, null at the end.
 */
export interface CommunityPeoplePage {
  people: CommunityAuthor[];
  next_after: string | null;
}

/** GET /api/communities/:communityId/moderators, GET …/bans */
export interface CommunityPeopleResponse {
  data: CommunityPeoplePage;
}

/** PUT …/bans/:userId — the messages of theirs the ban deleted (0 unless it was asked to). */
export interface CommunityBanResponse {
  data: { messages_deleted: number };
}

/** A message or forum post its room pins, and since when. */
export interface CommunityPinnedMessage extends CommunityMessage {
  pinned_at: string;
}

/**
 * A room's pinned messages or posts (at most `COMMUNITY_PINS_PER_ROOM_MAX`), the latest pinned
 * first, without those of authors the reader blocks; `authors` names the author of each.
 */
export interface CommunityPinsPage {
  messages: CommunityPinnedMessage[];
  authors: CommunityAuthor[];
}

/** GET /api/communities/:communityId/rooms/:roomId/pins */
export interface CommunityPinsResponse {
  data: CommunityPinsPage;
}

/** PUT …/messages/:messageId/pin — whether the message is now pinned, and since when. */
export interface CommunityPinResponse {
  data: { message_id: string; pinned_at: string | null };
}

/**
 * One moderation, as the community's log keeps it: who did it (`actor_user_id`), what, to whom
 * (`target_user_id`: the person acted on, or the author of the message or post), where (`room_id`,
 * `message_id`) and when. `messages_deleted` is a ban's count of the messages it deleted (null
 * when it was not asked to delete them; null on every other action).
 */
export interface CommunityModAction {
  action_id: number;
  action: CommunityModActionKind;
  actor_user_id: string;
  target_user_id: string | null;
  room_id: string | null;
  message_id: string | null;
  messages_deleted: number | null;
  created_at: string;
}

/**
 * A page of the moderation log, newest first; `people` names each entry's actor and target.
 * `next_before` continues toward older entries, null at the end.
 */
export interface CommunityModLogPage {
  actions: CommunityModAction[];
  people: CommunityAuthor[];
  next_before: number | null;
}

/** GET /api/communities/:communityId/mod-log */
export interface CommunityModLogResponse {
  data: CommunityModLogPage;
}

/**
 * A report in the moderators' inbox: what it is about and why, never who filed it. A message's
 * report keeps the message as its reporter saw it (`body`, and a post's `title`), so a later edit
 * cannot erase it (Decision 28). `resolved_at` is when a moderator or admin marked it handled.
 */
export interface CommunityReport {
  report_id: string;
  target: CommunityReportTarget;
  /** The person it is about: the message's author, or the person reported. */
  subject_user_id: string;
  room_id: string | null;
  message_id: string | null;
  body: string | null;
  title: string | null;
  reason: PeopleReportReason;
  /** The reporter's own note. */
  details: string | null;
  created_at: string;
  resolved_at: string | null;
}

/**
 * A page of the inbox, newest first; `people` names each report's subject. `next_before` continues
 * toward older reports, null at the end. A page may hold fewer than `COMMUNITY_REPORTS_PAGE_SIZE`
 * reports, as the reports about a moderator or an owner or admin of the org are left out.
 */
export interface CommunityReportsPage {
  reports: CommunityReport[];
  people: CommunityAuthor[];
  next_before: number | null;
}

/** GET /api/communities/:communityId/reports */
export interface CommunityReportsResponse {
  data: CommunityReportsPage;
}

/**
 * GET /api/communities/orgs/:orgId — the communities the org owns (at most
 * `COMMUNITIES_PER_ORG_MAX`), oldest first, for its owners and admins; never one that no longer
 * exists.
 */
export interface CommunityOrgCommunitiesResponse {
  data: CommunitySummary[];
}

/** A room's unread count for its reader: every message after their read position. */
export function communityRoomUnreadCount(room: Pick<CommunityRoomHead, 'last_seq' | 'read_seq'>): number {
  return Math.max(0, room.last_seq - room.read_seq);
}

/** A community's unread count for its reader, the Community badge's share of it: its rooms not archived. */
export function communityUnreadCount(head: Pick<CommunityHead, 'rooms'>): number {
  return head.rooms.reduce((sum, room) => sum + (room.archived ? 0 : communityRoomUnreadCount(room)), 0);
}
